<template>
  <div class="rounded-lg bg-gray-900 flex flex-col gap-4 overflow-hidden p-4">
    <div class="flex gap-2 items-center">
      <div class="flex flex-col gap-1 flex-1">
        <h2 class="text-text-secondary text-sm">{{ toTitleCase(props.stat.friendly_title || props.stat.title) }}</h2>
        <h1 class="text-text-primary text-lg font-medium">{{ formatStatString(props.stat.value) }}</h1>
      </div>
      <div class="flex gap-1">
        <h1 class="font-medium flex" :class="[props.style === 'strength' ? 'text-success-400' : 'text-danger-400']">
          {{ formatDifferenceString(props.stat.difference) }}
        </h1>
        <!-- <img src="../assets/icons/downarrow.svg" class="h-3 w-3" alt="Arrow down icon" /> -->
      </div>
    </div>

    <div class="w-full">
      <div class="w-full h-px bg-gray-800" />
    </div>

    <div v-if="style === 'strength'" class="text-sm text-text-secondary flex-1">
      {{ subject }}
      <span v-if="props.isMatch">{{ $t('matches.stat_block.in_match') }}</span>
      <span v-else>{{ $t('matches.stat_block.on_average') }}</span>
      <span class="text-success-400 pl-1">{{ $t('matches.stat_block.percent_better', { pct: Math.abs(props.stat.percentDifference) }) }}</span> {{ props.playerName ? $t('matches.stat_block.than_their_rank') : $t('matches.stat_block.than_your_rank') }}
    </div>
    <div v-else class="text-sm text-text-secondary flex-1">
      {{ subject }}
      <span v-if="props.isMatch">{{ $t('matches.stat_block.in_match') }}</span>
      <span v-else>{{ $t('matches.stat_block.on_average') }}</span>
      <span class="text-danger-400 pl-1">{{ $t('matches.stat_block.percent_worse', { pct: Math.abs(props.stat.percentDifference) }) }}</span> {{ props.playerName ? $t('matches.stat_block.than_their_rank') : $t('matches.stat_block.than_your_rank') }}
    </div>

    <div v-if="showImproveButton">
      <p class="text-xs text-text-secondary text-center pb-2">{{ $t('matches.stat_block.launch_training_server', { mod: props.stat.recommended_mods[0].modifier.friendly_name || 'Refrag' }) }}</p>
      <button @click="openServerModal(props.stat)" class="rounded-md py-2.5 w-full bg-primary-600 text-text-primary text-xs z-50 cursor-pointer hover:bg-primary-500 duration-300">
        {{ $t('matches.stat_block.improve_now') }}
      </button>
    </div>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  stat: {
    type: Object,
  },
  style: {
    type: String,
  },
  showImproveButton: {
    type: Boolean,
    default: true,
  },
  playerName: {
    type: String,
  },
  isMatch: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'open-server-modal',
]);

const { t } = useI18n();

function openServerModal() {
  emit('open-server-modal', props.stat);
}

function formatStatString(val) {
  let formattedStat;

  const value = parseFloat(val);

  switch (props.stat.unit_type) {
    case 'integer':
      formattedStat = Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.00$/, '');
      break;
    case 'percentage':
      formattedStat = `${(value * 100).toFixed(0)}%`;
      break;
    case 'decimal':
      formattedStat = value.toFixed(2);
      break;
    case 'degrees':
      formattedStat = `${value.toFixed(1)}°`;
      break;
    case 'seconds':
      formattedStat = `${value.round(stat)}s`;
      break;
    case 'nanoseconds':
      formattedStat = `${(value / 1000000000).toFixed(3)}s`;
      break;
    default:
      '';
      break;
  }
  return formattedStat;
}

function formatDifferenceString(val) {
  let formattedDifference = formatStatString(val);

  if (val > 0) {
    formattedDifference = `+${formattedDifference}`;
  }

  return formattedDifference;
}

function toTitleCase(str) {
  if (str == 'ShotsAtLowVelocityRate' || str == 'shots_at_low_velocity_rate') return t('matches.stat_block.full_movement_accuracy');

  return str
    .split('_') // Split the string by underscores
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize the first letter of each word
    .join(' '); // Join the words with a space
}

const subject = computed(() => {
  const stat = toTitleCase(props.stat.friendly_title || props.stat.title);
  return props.playerName
    ? t('matches.stat_block.subject_player', { name: props.playerName, stat })
    : t('matches.stat_block.subject_yours', { stat });
});

</script>
